import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { apiConnectorGet } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";
import { frontend } from "../../utils/APIRoutes";

export default function Promotion() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [tab, setTab] = useState("direct");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await apiConnectorGet(endpoint.member_details);
      if (res?.data?.success && res.data.result?.length > 0) {
        setData(res.data.result[0]);
      } else {
        toast.error(res?.data?.message || "Failed to load agency data");
      }
    } catch {
      toast.error("Failed to load agency data");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    const code = data?.lgn_cust_id || "";
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Invitation code copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    const code = data?.lgn_cust_id || "";
    const link = `${frontend}/register/referral?code=${code}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    toast.success("Invitation link copied!");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  if (loading) {
    return (
      <div style={s.loaderWrap}>
        <div style={s.spinner} />
        <p style={s.loaderText}>Loading agency data…</p>
      </div>
    );
  }

  const inviteCode = data?.lgn_cust_id || "N/A";
  const directMembers = data?.tr03_dir_mem ?? 0;
  const directTopupMem = data?.tr03_dir_topup_mem ?? 0;
  const directBusiness = data?.tr03_dir_buss ?? "0.00";
  const teamMembers = data?.tr03_team_mem ?? 0;
  const teamTopupMem = data?.tr03_topup_team_mem ?? 0;
  const teamBusiness = data?.tr03_team_buss ?? "0.00";

  const stats = [
    {
      label: "Total members",
      dVal: directMembers,
      tVal: teamMembers,
      icon: "👥",
    },
    {
      label: "Active (topup) members",
      dVal: directTopupMem,
      tVal: teamTopupMem,
      icon: "✅",
    },
    {
      label: "Total business",
      dVal: `$${parseFloat(directBusiness).toFixed(2)}`,
      tVal: `$${parseFloat(teamBusiness).toFixed(2)}`,
      icon: "💰",
    },
  ];

  return (
    <div style={s.root}>
      {/* Background */}
      <div style={s.grid} />
      <div style={s.glowTL} />
      <div style={s.glowBR} />

      <div style={s.container}>
        {/* ── Page Header ── */}
        <div style={s.pageHeader}>
          <div style={s.pageTitleWrap}>
            <span style={s.pageHeaderIcon}>🏆</span>
            <h1 style={s.pageTitle}>Agency</h1>
          </div>
        </div>

        {/* ── Wallet Stats Row ── */}
        <div style={s.walletRow}>
          <WalletBox
            label="Income Wallet"
            value={data?.tr03_inc_wallet}
            icon="💳"
          />
          <WalletBox
            label="Fund Wallet"
            value={data?.tr03_fund_wallet}
            icon="🏦"
          />
          <WalletBox
            label="Topup Wallet"
            value={data?.tr03_topup_wallet}
            icon="⬆️"
          />
        </div>

        {/* ── Invite Code Card ── */}
        <div style={s.referralCard}>
          <p style={s.referralLabel}>YOUR INVITATION CODE</p>
          <div style={s.referralRow}>
            <span style={s.referralValue}>{inviteCode}</span>
            <button
              style={{ ...s.copyBtn, ...(copied ? s.copyBtnCopied : {}) }}
              onClick={copyLink}
            >
              {copied ? "✓ Copied" : "⧉ Copy"}
            </button>
          </div>
          <p style={s.referralHint}>
            Share your code to invite friends and earn rewards
          </p>
        </div>

        {/* ── Invite Link Button ── */}
        <button
          style={{ ...s.inviteBtn, ...(copiedLink ? s.inviteBtnCopied : {}) }}
          onClick={copyLink}
        >
          {copiedLink ? "✓  LINK COPIED!" : "🔗  COPY INVITATION LINK"}
        </button>

        {/* ── Tab Switcher ── */}
        <div style={s.tabRow}>
          <button
            style={{ ...s.tab, ...(tab === "direct" ? s.tabActive : {}) }}
            onClick={() => setTab("direct")}
          >
            👥 Direct
          </button>
          <button
            style={{ ...s.tab, ...(tab === "team" ? s.tabActive : {}) }}
            onClick={() => setTab("team")}
          >
            🌐 Team
          </button>
        </div>

        {/* ── Stats Section ── */}
        <div style={s.section}>
          <div style={s.sectionHeader} className="!flex !justify-center">
            <span style={s.sectionIcon}>📊</span>
            <span style={s.sectionTitle}>
              {tab === "direct" ? "Direct Subordinates" : "Team Subordinates"}
            </span>
          </div>
          {stats.map((row, i) => (
            <div
              key={row.label}
              style={{
                ...s.statRow,
                ...(i === stats.length - 1 ? { borderBottom: "none" } : {}),
              }}
            >
              <div style={s.statLeft}>
                <span style={s.statIcon}>{row.icon}</span>
                <span style={s.statLabel}>{row.label}</span>
              </div>
              <span style={s.statValue}>
                {tab === "direct" ? row.dVal : row.tVal}
              </span>
            </div>
          ))}
        </div>

        {/* ── Action List ── */}
        <div style={s.section}>
          <div style={s.sectionHeader} className="!flex !justify-center">
            <span style={s.sectionIcon}>📋</span>
            <span style={s.sectionTitle} className="!font-extrabold">Quick Actions</span>
          </div>
          <ActionRow
            icon="📊"
            label="Subordinate data"
            arrow
            onClick={() => navigate("/SubordinateData")}
          />
          {/* <ActionRow
            icon="💰"
            label="Commission detail"
            arrow
            onClick={() => navigate("/CommissionDetail")}
          /> */}
          {/* <ActionRow icon="📜" label="Invitation rules"  arrow isLast /> */}
        </div>

        {/* ── History Buttons ── */}
        <div style={s.historySplitWrap}>
          <div style={s.historyCol}>
            <p style={s.historyColTitle} className="!text-white">Subscription Income</p>
            <div style={s.historyColButtons}>
              <HistoryBtn
                icon="🏅"
                label={"Referral\nIncome"}
                onClick={() => navigate("/SubscriptionDirectBonusHistory")}
              />
              <HistoryBtn
                icon="🏅"
                label={"Level\nIncome"}
                onClick={() => navigate("/SubscriptionLevelBonusHistory")}
              />
              <HistoryBtn
                icon="🎁"
                label={"Reward\nIncome"}
                onClick={() => navigate("/rewardBonusHistory")}
              />
            </div>
          </div>

          <div style={s.historyDivider} />

          <div style={s.historyCol}>
            <p style={s.historyColTitle} className="!text-white">Investment Income</p>
            <div style={s.historyColButtons}>
              <HistoryBtn
                icon="🏅"
                label={"Referral\nIncome"}
                onClick={() => navigate("/DirectBonusHistory")}
              />
              <HistoryBtn
                icon="🏅"
                label={"Level\nIncome"}
                onClick={() => navigate("/LevelBonusHistory")}
              />
              <HistoryBtn
                icon="🏅"
                label={"Bot Trading\nIncome"}
                onClick={() => navigate("/roiBonusHistory")}
              />

              <HistoryBtn
                icon="🎁"
                label={"Salary\nIncome"}
                onClick={() => navigate("/salaryBonusHistory")}
              />
              <HistoryBtn
                icon="🎁"
                label={"Reward\nIncome"}
                onClick={() => navigate("/rewardBonusHistory")}
              />
            </div>
          </div>
        </div>

        {/* ── Referral Income Note ── */}
        {/* <div style={s.noteCard}>
          <span style={s.noteIcon}>ℹ️</span>
          <p style={s.noteText}>
            <span style={s.noteLabel}>Please Note: </span>
            Referral Income is credited to your account once per calendar month.
          </p>
        </div> */}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700&display=swap');
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}

// ── Wallet Box ────────────────────────────────────────────────────────────────
function WalletBox({ label, value, icon }) {
  return (
    <div style={s.walletBox}>
      <span style={s.walletIcon}>{icon}</span>
      <p style={s.walletValue}>${parseFloat(value || 0).toFixed(2)}</p>
      <p style={s.walletLabel}>{label}</p>
    </div>
  );
}

// ── History Button ───────────────────────────────────────────────────────────
function HistoryBtn({ icon, label, onClick }) {
  return (
    <button style={s.historyBtn} onClick={onClick}>
      <span style={s.historyBtnIcon}>{icon}</span>
      <span style={s.historyBtnLabel}>{label}</span>
      <span style={s.historyBtnArrow}>›</span>
    </button>
  );
}

// ── Action Row ────────────────────────────────────────────────────────────────
function ActionRow({ icon, label, arrow, isLast, onClick }) {
  return (
    <div
      style={{
        ...s.actionRow,
        ...(isLast ? { borderBottom: "none" } : {}),
        ...(onClick ? { cursor: "pointer" } : {}),
      }}
      onClick={onClick}
    >
      <div style={s.actionLeft}>
        <span style={s.actionIcon}>{icon}</span>
        <span style={s.actionLabel}>{label}</span>
      </div>
      {arrow && <span style={s.actionArrow}>›</span>}
    </div>
  );
}

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  bg: "#020c12",
  card: "rgba(4,16,24,0.90)",
  border: "rgba(0,229,255,0.10)",
  cyan: "#00e5ff",
  cyanDim: "rgba(0,229,255,0.4)",
  text: "#e0f7fa",
  muted: "rgba(100,180,200,0.5)",
};

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  root: {
    minHeight: "100vh",
    background: C.bg,
    fontFamily: "'Sora',sans-serif",
    position: "relative",
    overflow: "hidden",
    paddingBottom: 100,
  },
  grid: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "linear-gradient(rgba(0,229,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,255,0.03) 1px,transparent 1px)",
    backgroundSize: "44px 44px",
  },
  glowTL: {
    position: "fixed",
    top: -150,
    left: -100,
    width: 480,
    height: 480,
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(circle,rgba(0,229,255,0.08) 0%,transparent 70%)",
  },
  glowBR: {
    position: "fixed",
    bottom: -100,
    right: -80,
    width: 380,
    height: 380,
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(circle,rgba(0,180,200,0.05) 0%,transparent 70%)",
  },
  container: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "24px 16px 40px",
    position: "relative",
    zIndex: 10,
    animation: "fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
  },

  // loader
  loaderWrap: {
    minHeight: "100vh",
    background: "#020c12",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
  },
  spinner: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "3px solid rgba(0,229,255,0.15)",
    borderTopColor: "#00e5ff",
    animation: "spin 0.75s linear infinite",
  },
  loaderText: {
    color: "rgba(100,180,200,0.5)",
    fontSize: 12,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.15em",
  },

  // page header
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 18,
  },
  pageTitleWrap: { display: "flex", alignItems: "center", gap: 10 },
  pageHeaderIcon: { fontSize: 22 },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: C.text,
    letterSpacing: "-0.02em",
    fontFamily: "'Sora',sans-serif",
  },

  // wallet row
  walletRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 10,
    marginBottom: 14,
  },
  walletBox: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "14px 10px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    backdropFilter: "blur(20px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  },
  walletIcon: { fontSize: 18 },
  walletValue: {
    fontSize: 13,
    fontWeight: 700,
    color: C.cyan,
    fontFamily: "'Space Mono',monospace",
    textAlign: "center",
  },
  walletLabel: {
    fontSize: 9,
    color: C.muted,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    textAlign: "center",
  },

  // referral card
  referralCard: {
    background:
      "linear-gradient(135deg,rgba(0,229,255,0.06) 0%,rgba(0,180,200,0.03) 100%)",
    border: "1px solid rgba(0,229,255,0.2)",
    borderRadius: 16,
    padding: "18px 20px",
    marginBottom: 14,
  },
  referralLabel: {
    fontSize: 9,
    color: C.cyanDim,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  referralRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  referralValue: {
    fontSize: 18,
    fontWeight: 700,
    color: C.cyan,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.08em",
  },
  copyBtn: {
    background: "rgba(0,229,255,0.1)",
    border: "1px solid rgba(0,229,255,0.25)",
    color: C.cyan,
    borderRadius: 8,
    padding: "6px 16px",
    fontSize: 11,
    fontFamily: "'Space Mono',monospace",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.08em",
    transition: "all 0.2s",
  },
  copyBtnCopied: {
    background: "rgba(0,230,118,0.1)",
    border: "1px solid rgba(0,230,118,0.3)",
    color: "#00e676",
  },
  referralHint: {
    fontSize: 10,
    color: C.muted,
    marginTop: 10,
    lineHeight: 1.5,
  },

  // invite button
  inviteBtn: {
    width: "100%",
    height: 50,
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(90deg,#00e5ff,#00b8d4)",
    color: "#020c12",
    fontSize: 12,
    fontWeight: 700,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.12em",
    cursor: "pointer",
    marginBottom: 14,
    boxShadow: "0 6px 28px rgba(0,229,255,0.22)",
    transition: "all 0.2s",
  },
  inviteBtnCopied: {
    background: "linear-gradient(90deg,#00e676,#00c853)",
    boxShadow: "0 6px 28px rgba(0,230,118,0.22)",
  },

  // tabs
  tabRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginBottom: 12,
  },
  tab: {
    height: 42,
    borderRadius: 12,
    border: "1px solid rgba(0,229,255,0.12)",
    background: "rgba(4,16,24,0.8)",
    color: C.muted,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "'Sora',sans-serif",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  tabActive: {
    background: "rgba(0,229,255,0.08)",
    border: "1px solid rgba(0,229,255,0.35)",
    color: C.cyan,
    boxShadow: "0 0 16px rgba(0,229,255,0.08)",
  },

  // section
  section: {
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 18,
    overflow: "hidden",
    backdropFilter: "blur(20px)",
    marginBottom: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "14px 18px 12px",
    borderBottom: `1px solid ${C.border}`,
    background: "rgba(0,229,255,0.025)",
  },
  sectionIcon: { fontSize: 16 },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
    letterSpacing: "-0.01em",
  },

  // stat rows
  statRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "13px 18px",
    borderBottom: "1px solid rgba(0,229,255,0.05)",
  },
  statLeft: { display: "flex", alignItems: "center", gap: 12 },
  statIcon: { fontSize: 16 },
  statLabel: { fontSize: 12, color: C.muted },
  statValue: {
    fontSize: 15,
    fontWeight: 700,
    color: C.cyan,
    fontFamily: "'Space Mono',monospace",
  },

  // history buttons — split into two columns divided by a vertical line
  historySplitWrap: {
    display: "flex",
    alignItems: "stretch",
    gap: 14,
    marginBottom: 12,
  },
  historyCol: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  historyColTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: C.cyanDim,
    fontFamily: "'Space Mono',monospace",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 10,
  },
  historyColButtons: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  historyDivider: {
    width: 1,
    alignSelf: "stretch",
    background: C.border,
    flexShrink: 0,
  },
  historyBtn: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 16,
    padding: "16px 14px",
    cursor: "pointer",
    backdropFilter: "blur(20px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    textAlign: "left",
    width: "100%",
  },
  historyBtnIcon: { fontSize: 22, flexShrink: 0 },
  historyBtnLabel: {
    flex: 1,
    fontSize: 12,
    fontWeight: 600,
    color: C.text,
    lineHeight: 1.4,
    whiteSpace: "pre-line",
  },
  historyBtnArrow: { fontSize: 20, color: C.muted, flexShrink: 0 },

  // action rows
  actionRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "15px 18px",
    borderBottom: "1px solid rgba(0,229,255,0.05)",
  },
  actionLeft: { display: "flex", alignItems: "center", gap: 12 },
  actionIcon: { fontSize: 16 },
  actionLabel: { fontSize: 13, color: C.text, fontWeight: 500 },
  actionArrow: { fontSize: 20, color: C.muted, lineHeight: 1 },

  // referral note
  noteCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    background: "rgba(0,229,255,0.04)",
    border: "1px solid rgba(0,229,255,0.15)",
    borderLeft: "3px solid rgba(0,229,255,0.5)",
    borderRadius: 12,
    padding: "12px 16px",
    marginBottom: 12,
  },
  noteIcon: { fontSize: 14, flexShrink: 0, marginTop: 1 },
  noteLabel: {
    fontWeight: 700,
    color: C.cyan,
  },
  noteText: {
    fontSize: 11,
    color: C.muted,
    lineHeight: 1.6,
    fontFamily: "'Sora',sans-serif",
  },
};
